"use client";

import { useState, useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { api } from "@/lib/trpc/client";

type TimeRange = "30d" | "90d" | "1y" | "all";
type ThresholdFilter = "all" | "1M" | "10M" | "100M";

interface ChainWhaleChartProps {
  slug: string;
}

const THRESHOLD_COLORS: Record<string, { stroke: string; fill: string }> = {
  "1M": { stroke: "#06b6d4", fill: "#06b6d4" },
  "10M": { stroke: "#f59e0b", fill: "#f59e0b" },
  "100M": { stroke: "#ef4444", fill: "#ef4444" },
};

function formatCount(value: number): string {
  if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(0)}K`;
  return value.toLocaleString();
}

function formatUsd(value: number): string {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

interface MergedDataPoint {
  date: string;
  "1M_count"?: number;
  "10M_count"?: number;
  "100M_count"?: number;
  "1M_balance"?: number;
  "10M_balance"?: number;
  "100M_balance"?: number;
}

export function ChainWhaleChart({ slug }: ChainWhaleChartProps) {
  const [range, setRange] = useState<TimeRange>("90d");
  const [thresholdFilter, setThresholdFilter] = useState<ThresholdFilter>("all");
  const [showBalance, setShowBalance] = useState(false);

  const days = range === "30d" ? 30 : range === "90d" ? 90 : range === "1y" ? 365 : 365;

  const { data: rawData, isLoading } = api.onchain.getWhaleDistribution.useQuery(
    {
      slug,
      days,
      ...(thresholdFilter !== "all" ? { threshold: thresholdFilter } : {}),
    },
    { staleTime: 5 * 60 * 1000 },
  );

  const mergedData = useMemo(() => {
    if (!rawData) return [];

    const byDate: Record<string, MergedDataPoint> = {};

    for (const s of rawData) {
      if (!byDate[s.date]) {
        byDate[s.date] = { date: s.date };
      }
      const key = s.threshold;
      byDate[s.date]![`${key}_count` as keyof MergedDataPoint] = s.walletCount as never;
      byDate[s.date]![`${key}_balance` as keyof MergedDataPoint] = s.totalBalance as never;
    }

    return Object.values(byDate).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
  }, [rawData]);

  const filteredData = useMemo(() => {
    if (range === "all") return mergedData;
    const now = Date.now();
    const cutoffDays = range === "30d" ? 30 : range === "90d" ? 90 : 365;
    const cutoff = now - cutoffDays * 86400 * 1000;
    return mergedData.filter((d) => new Date(d.date).getTime() >= cutoff);
  }, [mergedData, range]);

  const activeThresholds = thresholdFilter === "all"
    ? (["1M", "10M", "100M"] as const)
    : [thresholdFilter] as const;

  const ranges: { key: TimeRange; label: string }[] = [
    { key: "30d", label: "30D" },
    { key: "90d", label: "90D" },
    { key: "1y", label: "1Y" },
    { key: "all", label: "All" },
  ];

  const thresholds: { key: ThresholdFilter; label: string }[] = [
    { key: "all", label: "All Tiers" },
    { key: "1M", label: "> $1M" },
    { key: "10M", label: "> $10M" },
    { key: "100M", label: "> $100M" },
  ];

  if (isLoading) {
    return (
      <div className="h-[300px] flex items-center justify-center text-gray-500">
        Loading whale data...
      </div>
    );
  }

  if (!filteredData.length) {
    return (
      <div className="h-[300px] flex items-center justify-center text-gray-500">
        No whale distribution data available yet. Data builds up from weekly syncs.
      </div>
    );
  }

  const suffix = showBalance ? "_balance" : "_count";

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 mb-4">
        {/* Time range */}
        <div className="flex items-center gap-1">
          {ranges.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                range === r.key
                  ? "bg-blue-600 text-white"
                  : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Threshold filter */}
        <div className="flex items-center gap-1">
          {thresholds.map((t) => (
            <button
              key={t.key}
              onClick={() => setThresholdFilter(t.key)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                thresholdFilter === t.key
                  ? "bg-cyan-600 text-white"
                  : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Toggle count vs balance */}
        <button
          onClick={() => setShowBalance(!showBalance)}
          className="px-3 py-1 rounded-lg text-xs font-medium bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
        >
          {showBalance ? "Show Wallet Count" : "Show Total Balance"}
        </button>
      </div>

      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={filteredData}>
            <defs>
              {activeThresholds.map((t) => (
                <linearGradient key={t} id={`whaleGradient${t}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={THRESHOLD_COLORS[t]!.fill} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={THRESHOLD_COLORS[t]!.fill} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <XAxis
              dataKey="date"
              tickFormatter={(d: string) => {
                const date = new Date(d);
                return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
              }}
              stroke="#4b5563"
              tick={{ fill: "#6b7280", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              minTickGap={40}
            />
            <YAxis
              tickFormatter={showBalance ? formatUsd : formatCount}
              stroke="#4b5563"
              tick={{ fill: "#6b7280", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={70}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(230,15%,12%)",
                border: "1px solid hsl(230,15%,20%)",
                borderRadius: "8px",
                color: "#fff",
                fontSize: "13px",
              }}
              labelFormatter={(d) =>
                new Date(String(d)).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })
              }
              formatter={(value, name) => {
                const v = Number(value);
                const label = String(name).replace(suffix, "");
                const formatted = showBalance ? formatUsd(v) : formatCount(v);
                return [formatted, `>${label} Tier`];
              }}
            />
            {activeThresholds.length > 1 && <Legend />}
            {activeThresholds.map((t) => (
              <Area
                key={t}
                type="monotone"
                dataKey={`${t}${suffix}`}
                name={`>${t}`}
                stroke={THRESHOLD_COLORS[t]!.stroke}
                strokeWidth={2}
                fill={`url(#whaleGradient${t})`}
                dot={false}
                animationDuration={800}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
